import { useNavigate } from 'react-router-dom';
import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import styles from "../../assets/styles/Homepage/YogaRetreat.module.css";
import dividerImg from "../../assets/images/linedesign.png";
import img1 from "../../assets/images/courses/1.png";
import img2 from "../../assets/images/courses/2.png";
import img3 from "../../assets/images/courses/3.png";
const ENV_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

const API_BASE = `${ENV_API_BASE_URL}/api`;
const ASSET_BASE = `${ENV_API_BASE_URL}`;
const DEFAULT_SECTION = {
  title: "Yoga Meditation Retreat",
  highlightText: "in Rishikesh",
  description:
    "Elevate your life with an unforgettable yoga retreat experience. Discover and book rejuvenating yoga retreats, vacations, and yoga teacher training courses at Hatha Yogashram",
};

const toAssetUrl = (value) => {
  if (!value) return "";
  if (typeof value !== "string") return value;
  if (value.startsWith("http") || value.startsWith("data:")) return value;
  return `${ASSET_BASE}/${value.replace(/^\/+/, "")}`;
};

const DEFAULT_RETREATS = [
  {
    days: "5 Days Yoga Retreat",
    img: img1,
    private: "₹22,500 ($261)",
    shared: "₹15,500 ($180)",
    path: "/5-days-yoga-retreat",
  },
  {
    days: "10 Days Yoga Retreat",
    img: img2,
    private: "₹40,000 ($464)",
    shared: "₹31,000 ($359)",
    path: "/10-days-yoga-retreat",
  },
  {
    days: "20 Days Yoga Retreat",
    img: img3,
    private: "₹85,000 ($985)",
    shared: "₹64,000 ($742)",
    path: "/20-days-yoga-retreat",
  },
];

/* ===================== */
/* Framer Motion Variants */
/* ===================== */

const headingVariant = {
  hidden: { opacity: 0, y: 25 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: "easeOut" },
  },
};

const gridVariant = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.18,
    },
  },
};

const cardVariant = {
  hidden: { opacity: 0, y: 35, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const YogaRetreat = () => {
  const navigate = useNavigate();
  const [section, setSection] = useState(DEFAULT_SECTION);
  const [retreats, setRetreats] = useState(DEFAULT_RETREATS);

  useEffect(() => {
    const fetchRetreatSection = async () => {
      try {
        const res = await axios.get(`${API_BASE}/home-retreat-section`);
        if (res.data?.data) {
          setSection({
            title: res.data.data.title || DEFAULT_SECTION.title,
            highlightText: res.data.data.highlightText || DEFAULT_SECTION.highlightText,
            description: res.data.data.description || DEFAULT_SECTION.description,
          });
        }
      } catch (error) {
        console.error("Failed to load retreat section", error);
      }
    };

    const fetchRetreats = async () => {
      try {
        const res = await axios.get(`${API_BASE}/courses`, {
          params: { homeSection: "retreat" },
        });
        const items = res.data?.data || [];
        if (!items.length) return;

        const mapped = items.map((course, index) => {
          const details = course.retreat || {};
          return {
            days: details.title || course.title || `Retreat ${index + 1}`,
            img: toAssetUrl(details.image) || DEFAULT_RETREATS[index % DEFAULT_RETREATS.length].img,
            private: details.privatePrice || course.home?.privatePrice || "",
            shared: details.sharedPrice || course.home?.sharedPrice || "",
            path: details.path || course.legacyPath || (course.slug ? `/course/${course.slug}` : "/BookingForm"),
          };
        });

        setRetreats(mapped);
      } catch (error) {
        console.error("Failed to load retreat courses", error);
      }
    };

    fetchRetreatSection();
    fetchRetreats();
  }, []);

  return (
    <motion.section
      className={styles.section}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
    >
      <div className="container">
        {/* Heading */}
        <motion.div
          className={styles.headingWrapper}
          variants={headingVariant}
        >
          <h2 className={styles.heading}>
            {section.title} <span>{section.highlightText}</span>
          </h2>

          <p className={styles.subText}>
            {section.description}
          </p>

          <div className={styles.divider}>
            <img src={dividerImg} alt="yoga divider" />
          </div>
        </motion.div>

        {/* Cards */}
        <motion.div
          className={styles.cardGrid}
          variants={gridVariant}
        >
          {retreats.map((item, index) => (
            <motion.div
              className={styles.card}
              key={index}
              variants={cardVariant}
              whileHover={{
                y: -8,
                boxShadow: "0 15px 35px rgba(0,0,0,0.15)",
              }}
              transition={{ type: "spring", stiffness: 200 }}
            >
              <motion.div
                className={styles.imageBox}
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.4 }}
              >
                <img src={item.img} alt={item.days} />
              </motion.div>

              <h4 className={styles.cardTitle}>{item.days}</h4>

              <p className={styles.price}>
                <strong>Private Stay</strong> {item.private}
              </p>
              <p className={styles.price}>
                <strong>Shared Stay</strong> {item.shared}
              </p>

              <div className={styles.btnGroup}>
                <motion.button
                  className={styles.outlineBtn}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate(item.path)}
                >
                  Course
                  <br />
                  Details
                </motion.button>

                <motion.button
                  className={styles.outlineBtn}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/BookingForm')}
                >
                  <span className={styles.btnText}>
                    Book
                    <span>Now</span>
                  </span>
                </motion.button>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </motion.section>
  );
};

export default YogaRetreat;
